import styles from "./MessageCard.module.css";
import avatarImage from "../../assets/icons/avatar.png";
import { useState, useEffect, useRef } from "react";
import { ENDPOINTS } from "../../utils/endpoints";
import api from "../../services/api";
import useAuthStore from "../../store/authStore";
import chatStore from "../../store/chatStore";
import { FiDownload } from "react-icons/fi";
import {
  FaFilePdf,
  FaFileWord,
  FaFileExcel,
  FaEdit,
  FaTrash,
  FaSave,
  FaTimes,
  FaReply,
} from "react-icons/fa";
import { saveAs } from "file-saver";
import axios from "axios";
import {
  InstagramEmbed,
  TikTokEmbed,
  LinkedInEmbed,
  FacebookEmbed,
} from "react-social-media-embed";

const EMOJI_OPTIONS = ["👍", "❤️", "😍", "🔥", "💯"];

export function formatDateTimeParts(input) {
  if (!input) return { date: "-", time: "-" };

  const dateObj = new Date(input);
  if (isNaN(dateObj.getTime())) return { date: "-", time: "-" };

  const date = dateObj.toLocaleDateString("en-GB");
  const time = dateObj.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });

  return { date, time };
}

function renderMessage(message) {
  if (!message || message.trim() === "") return null;

  // Regular expression to match URLs
  const urlRegex = /(https?:\/\/[^\s]+)/g;

  // Split the message by URLs while keeping the URLs
  const parts = message.split(urlRegex);

  return parts.map((part, index) => {
    // Check if this part is a URL
    if (part.match(/^https?:\/\//)) {
      return (
        <a
          key={index}
          href={part}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.messageLink}
          onClick={(e) => e.stopPropagation()}
        >
          {part}
        </a>
      );
    }

    // Check for mentions in non-URL parts
    const words = part.split(/(\s+)/);
    return words.map((word, wordIndex) => {
      if (word.startsWith("@")) {
        return (
          <span key={`${index}-${wordIndex}`} className={styles.mention}>
            {word}
          </span>
        );
      }

      return <span key={`${index}-${wordIndex}`}>{word}</span>;
    });
  });
}

// Helper functions to detect social media URLs
function isInstagramUrl(url) {
  return (
    url &&
    (url.includes("instagram.com/p/") || url.includes("instagram.com/reel/"))
  );
}

function isTikTokUrl(url) {
  return url && url.includes("tiktok.com");
}

function isLinkedInUrl(url) {
  return url && url.includes("linkedin.com/");
}

// function isFacebookUrl(url) {
//   return (
//     url &&
//     (url.includes("facebook.com/") ||
//       url.includes("fb.com/") ||
//       url.includes("fb.watch/"))
//   );
// }

function extractFirstUrl(message) {
  if (!message) return null;
  const urlRegex = /(https?:\/\/[^\s]+)/gi;
  const matches = message.match(urlRegex);
  return matches ? matches[0] : null;
}

const MessageCard = ({
  sender,
  messageData,
  channel,
  currentUserId,
  currentUserRole,
  onEdit,
  onDelete,
  onReply,
  users,
}) => {
  const { user, socket } = useAuthStore();
  const { newMessageReactionRecieved, setNewMessageReactionRecieved } =
    chatStore();
  const { date, time } = formatDateTimeParts(messageData.created_at);
  const {
    file_type: fileType,
    file_url: fileUrl,
    message,
    sender_id,
    sender_profile_picture,
  } = messageData;

  const [showPicker, setShowPicker] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState(null);
  const [imageLoading, setImageLoading] = useState(true);
  const [linkPreview, setLinkPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const editInputRef = useRef(null);

  const openImageModal = (url) => {
    setModalImageUrl(url);
    setShowImageModal(true);
  };

  const closeImageModal = (e) => {
    if (e.target.classList.contains(styles.imageModalOverlay)) {
      setShowImageModal(false);
      setModalImageUrl(null);
    }
  };

  const hasReactions =
    Array.isArray(messageData.reactions) &&
    messageData.reactions.some((r) => (r.count ?? 0) > 0);

  const [isEditing, setIsEditing] = useState(false);
  const [editedMessage, setEditedMessage] = useState("");
  const isOwnMessage = sender_id === currentUserId;
  const isAdmin = currentUserRole === "administrator";

  const canEdit =
    (isOwnMessage || isAdmin) &&
    message &&
    message.trim() !== "" &&
    !messageData.is_deleted;

  const canDelete = (isOwnMessage || isAdmin) && !messageData.is_deleted;

  const getReplySenderName = () => {
    if (!messageData.reply_to_sender_id) return "";
    const replySender = users?.find(
      (u) => u.id === messageData.reply_to_sender_id
    );
    return replySender?.display_name || replySender?.username || "User";
  };
  const handleReply = (e) => {
    e.stopPropagation();
    onReply(messageData);
  };
  const handleReplyClick = async (e) => {
    e.stopPropagation();

    if (!messageData.reply_to_message_id) return;

    // First, try to find the message in the current DOM
    const element = document.getElementById(
      `msg-${messageData.reply_to_message_id}`
    );

    if (element) {
      // Message is already visible, just scroll to it
      element.scrollIntoView({ behavior: "smooth", block: "center" });

      // Add highlight effect
      element.classList.add(styles.highlightMessage);
      setTimeout(() => {
        element.classList.remove(styles.highlightMessage);
      }, 2000);
    } else {
      // Message is not loaded, need to fetch it via context
      // Call the parent component's jump function
      if (channel?.onJumpToMessage) {
        channel.onJumpToMessage(messageData.reply_to_message_id);
      }
    }
  };
  const handleSaveEdit = () => {
    const trimmed = editedMessage.trim();
    if (trimmed && trimmed !== message) {
      onEdit(messageData.id, trimmed);
    }
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setEditedMessage(message);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSaveEdit();
    } else if (e.key === "Escape") {
      handleCancelEdit();
    }
  };

  const downloadImage = () => saveAs(fileUrl, "image.jpg");
  const downloadOtherFileType = () => {
    const filename = fileUrl.split("/").pop() || "file";
    saveAs(fileUrl, filename);
  };

  const handleCardClick = () => {
    if (!isEditing) {
      setShowPicker(!showPicker);
    }
  };

  const handleEmojiClick = async (emoji) => {
    setShowPicker(false);
    try {
      const payload = { user_id: user.id, emoji };
      const res = await api.post(
        ENDPOINTS.REACT_TO_A_MESSAGE(messageData.id),
        payload
      );
      if (res.data.success) {
        socket.emit("send-message-reaction", {
          type: res.data.data.type,
          channelId: channel.id,
          userId: user.id,
          messageId: messageData.id,
          emoji: emoji,
          removedEmoji: res.data.data.removedEmoji,
        });
      }
    } catch (error) {
      console.error("Error toggling reaction:", error);
    }
  };

  useEffect(() => {
    if (isEditing && editInputRef.current) {
      const textarea = editInputRef.current;
      const length = editedMessage.length;

      // Set focus and move cursor to end
      textarea.focus();
      textarea.setSelectionRange(length, length);

      // Scroll to the end for longer text
      textarea.scrollTop = textarea.scrollHeight;
    }
  }, [isEditing, editedMessage]);

  const renderReactions = () => {
    const hasAnyReactions =
      Array.isArray(messageData.reactions) &&
      messageData.reactions.some((r) => (r.count ?? 0) > 0);

    if (!showPicker && !hasAnyReactions) return null;

    return (
      <div className={styles.reactions}>
        {EMOJI_OPTIONS.map((emoji, index) => {
          const count =
            messageData.reactions.find((r) => r.emoji === emoji)?.count ?? 0;

          return (
            <button
              key={index}
              className={`${styles.reactionButton} ${
                messageData.user_reaction === emoji
                  ? styles.activeEmojiButton
                  : ""
              }`}
              onClick={() => handleEmojiClick(emoji)}
            >
              <span className={styles.emoji}>{emoji}</span>
              <span className={styles.count}>{count}</span>
            </button>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    if (!messageData.reactions || messageData.reactions.length === 0) return;

    const total = messageData.reactions.reduce(
      (sum, r) => sum + (r.count ?? 0),
      0
    );

    if (total === 0) {
      setShowPicker(false);
    }
  }, [messageData.reactions.map((r) => r.count).join(",")]);

  const renderApplicationPreview = (url) => {
    const ext = url.split(".").pop().toLowerCase();
    let Icon = null;
    if (ext === "pdf") Icon = FaFilePdf;
    else if (ext === "doc" || ext === "docx") Icon = FaFileWord;
    else if (ext === "xls" || ext === "xlsx") Icon = FaFileExcel;

    return (
      <div className={styles.fileWrapper}>
        {Icon && <Icon size={40} className={styles.fileIcon} />}
        <span className={styles.fileName}>{url.split("/").pop()}</span>
        <div
          onClick={(e) => {
            e.stopPropagation();
            downloadOtherFileType();
          }}
          className={styles.downloadIcon}
        >
          <FiDownload size={18} />
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (!newMessageReactionRecieved) return;
    if (messageData.id === newMessageReactionRecieved.messageId) {
      //1:add, 2:update, 3:delete
      if (newMessageReactionRecieved.type === 1) {
        const reaction = messageData.reactions.find(
          (m) => m.emoji === newMessageReactionRecieved.emoji
        );
        if (reaction) {
          reaction.count = (reaction.count ?? 0) + 1;
        } else {
          const newReaction = {
            emoji: newMessageReactionRecieved.emoji,
            count: 1,
          };
          messageData.reactions.push(newReaction);
        }
        if (newMessageReactionRecieved.userId === user.id) {
          messageData.user_reaction = newMessageReactionRecieved.emoji;
        }
      } else if (newMessageReactionRecieved.type === 2) {
        //Remove old one
        const reactionRemoved = messageData.reactions.find(
          (m) => m.emoji === newMessageReactionRecieved.removedEmoji
        );
        if (reactionRemoved) {
          reactionRemoved.count = (reactionRemoved.count ?? 0) - 1;
        } else {
          const newReaction = {
            emoji: newMessageReactionRecieved.removedEmoji,
            count: 0,
          };
          messageData.reactions.push(newReaction);
        }

        //Add new one
        const reaction = messageData.reactions.find(
          (m) => m.emoji === newMessageReactionRecieved.emoji
        );
        if (reaction) {
          reaction.count = (reaction.count ?? 0) + 1;
        } else {
          const newReaction = {
            emoji: newMessageReactionRecieved.emoji,
            count: 1,
          };
          messageData.reactions.push(newReaction);
        }

        if (newMessageReactionRecieved.userId === user.id) {
          messageData.user_reaction = newMessageReactionRecieved.emoji;
        }
      } else {
        const reaction = messageData.reactions.find(
          (m) => m.emoji === newMessageReactionRecieved.emoji
        );
        if (reaction) {
          reaction.count = (reaction.count ?? 0) - 1;
        } else {
          const newReaction = {
            emoji: newMessageReactionRecieved.emoji,
            count: 0,
          };
          messageData.reactions.push(newReaction);
        }

        if (newMessageReactionRecieved.userId === user.id) {
          messageData.user_reaction = null;
        }
      }
      setNewMessageReactionRecieved(null);
    }
  }, [
    newMessageReactionRecieved,
    setNewMessageReactionRecieved,
    messageData.id,
    user.id,
  ]);

  // Fetch link preview for non-social media URLs
  useEffect(() => {
    const url = extractFirstUrl(message);
    if (!url) return;

    // Skip if it's any social media platform (handled by embeds)
    if (isInstagramUrl(url) || isLinkedInUrl(url)) {
      setLoadingPreview(false);
      return;
    }

    const fetchPreview = async () => {
      setLoadingPreview(true);
      setImageLoading(true);
      try {
        const { data } = await api.get(ENDPOINTS.GET_LINK_PREVIEW(url));

        if (data.success && data.preview) {
          setLinkPreview({
            url: data.preview.url || url,
            title: data.preview.title || "No title",
            description: data.preview.description || "",
            image: data.preview.image || null,
          });
        }
      } catch (err) {
        console.error("Preview fetch error", err);
      } finally {
        setLoadingPreview(false);
        setImageLoading(false);
      }
    };

    fetchPreview();
  }, [message]);

  const senderAvatar = sender_profile_picture || avatarImage;

  if (messageData.is_deleted) {
    return null;
  }

  const firstUrl = extractFirstUrl(message);
  const isInstagram = firstUrl && isInstagramUrl(firstUrl);
  const isLinkedIn = firstUrl && isLinkedInUrl(firstUrl);
  // const isFacebook = firstUrl && isFacebookUrl(firstUrl);
  const showRegularPreview = firstUrl && !isInstagram && !isLinkedIn;

  return (
    <>
      <div
        className={`${styles.card} ${
          sender_id === user.id ? styles.activeCard : ""
        }`}
        onClick={handleCardClick}
      >
        <img src={senderAvatar} className={styles.avatar} alt="user" />

        <div className={styles.chatCard}>
          <div className={styles.userInfo}>
            <div className={styles.username}>{sender}</div>
            <div className={styles.datetime}>
              {date} {time}
              {messageData.updated_at && !isEditing && (
                <span className={styles.editedLabel}> (edited)</span>
              )}
            </div>
            {(canEdit || canDelete || onReply) && !isEditing && (
              <div className={styles.messageActions}>
                {onReply && (
                  <button
                    onClick={handleReply}
                    className={styles.actionBtn}
                    title="Reply to message"
                  >
                    <FaReply />
                  </button>
                )}
                {canEdit && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditedMessage(messageData.message || "");
                      setIsEditing(true);
                    }}
                    className={styles.actionBtn}
                    title="Edit message"
                  >
                    <FaEdit />
                  </button>
                )}
                {canDelete && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(messageData.id);
                    }}
                    className={styles.actionBtn}
                    title="Delete message"
                  >
                    <FaTrash />
                  </button>
                )}
              </div>
            )}
          </div>
          {isEditing ? (
            <div className={styles.editContainer}>
              <textarea
                value={editedMessage}
                ref={editInputRef}
                onChange={(e) => setEditedMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                className={styles.editInput}
                rows={3}
              />
              <div className={styles.editActions}>
                <button onClick={handleSaveEdit} className={styles.saveBtn}>
                  <FaSave /> Save
                </button>
                <button onClick={handleCancelEdit} className={styles.cancelBtn}>
                  <FaTimes /> Cancel
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Reply Preview - Show what message this is replying to */}
              {messageData.reply_to_message_id && (
                <div
                  className={styles.replyIndicator}
                  onClick={handleReplyClick}
                >
                  <div className={styles.replyBar}></div>
                  <div className={styles.replyDetails}>
                    <div className={styles.replySenderName}>
                      {getReplySenderName()}
                    </div>
                    <div className={styles.replyMessageText}>
                      {messageData.reply_to_message || "[Message]"}
                    </div>
                  </div>
                </div>
              )}

              {message && message.trim() !== "" && (
                <div className={styles.message}>{renderMessage(message)}</div>
              )}

              {/* Instagram Embed */}
              {isInstagram && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "10px",
                    marginBottom: "10px",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <InstagramEmbed url={firstUrl} width={328} />
                </div>
              )}

              {/* LinkedIn Embed */}
              {isLinkedIn && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "10px",
                    marginBottom: "10px",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <LinkedInEmbed
                    url={firstUrl}
                    postUrl={firstUrl}
                    width={325}
                    height={570}
                  />
                </div>
              )}

              {/* Facebook Embed */}
              {/* {isFacebook && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    marginTop: "10px",
                    marginBottom: "10px",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <FacebookEmbed url={firstUrl} width={325} />
                </div>
              )} */}

              {/* Regular Link Preview Loading */}
              {showRegularPreview && linkPreview === null && loadingPreview && (
                <div className={styles.linkPreview}>
                  <div className={styles.skeletonImage}></div>
                </div>
              )}

              {/* Regular Link Preview */}
              {showRegularPreview && linkPreview !== null && (
                <div className={styles.linkPreview}>
                  {linkPreview.image && (
                    <div className={styles.linkImageContainer}>
                      <img
                        src={linkPreview.image}
                        alt="preview"
                        className={styles.linkImage}
                        onClick={(e) => {
                          e.stopPropagation();
                          window.open(
                            linkPreview.url,
                            "_blank",
                            "noopener,noreferrer"
                          );
                        }}
                        onLoad={() => setImageLoading(false)}
                        onError={() => setImageLoading(false)}
                        style={{
                          cursor: "pointer",
                          display: imageLoading ? "none" : "block",
                        }}
                      />
                    </div>
                  )}

                  <div className={styles.linkContent}>
                    <div className={styles.linkTitle}>{linkPreview.title}</div>

                    <div className={styles.linkDesc}>
                      {linkPreview.description}
                    </div>
                    <a
                      href={linkPreview.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.linkUrl}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {linkPreview.url}
                    </a>
                  </div>
                </div>
              )}

              {fileType === "image" && fileUrl && (
                <div className={styles.imageWrapper}>
                  <img
                    src={fileUrl}
                    alt="chat media"
                    className={styles.mediaPreview}
                    onClick={(e) => {
                      e.stopPropagation();
                      openImageModal(fileUrl);
                    }}
                  />
                  <div
                    onClick={(e) => {
                      e.stopPropagation();
                      downloadImage();
                    }}
                    className={styles.downloadIcon}
                  >
                    <FiDownload size={18} />
                  </div>
                </div>
              )}

              {fileType === "video" && fileUrl && (
                <video controls className={styles.mediaPreview}>
                  <source src={fileUrl} type="video/mp4" />
                </video>
              )}

              {fileType === "application" &&
                fileUrl &&
                renderApplicationPreview(fileUrl)}

              {renderReactions()}
            </>
          )}
        </div>
      </div>

      {showImageModal && (
        <div className={styles.imageModalOverlay} onClick={closeImageModal}>
          <div className={styles.imageModalContent}>
            <img src={modalImageUrl} alt="Full preview" />
          </div>
        </div>
      )}
    </>
  );
};

export default MessageCard;
